import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible'
import {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogClose,
} from '@radix-ui/react-dialog'
import { Form, Link, useSubmit } from '@remix-run/react'
import { type PropsWithChildren, useRef } from 'react'
import { ChangeThemeForm } from '#app/components/forms/change-theme'
import { TeamLogo } from '#app/components/team-logo'
import { Button } from '#app/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import { Icon } from '#app/components/ui/icon'
import { type Theme } from '#app/utils/theme.server'
import { useOptionalUser } from '#app/utils/user'
import { Spacer } from './spacer'

type TeamsProp = {
  id: number
  city: string
  teamLogos: { id: string; altText: string | null }[]
}

function MobileNavHeader({
  theme,
  children,
  id,
}: PropsWithChildren<{ theme: Theme | null; id: string }>) {
  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
      <Link to="/" className="focusable group grid leading-snug">
        <span className="font-light text-accent-11 transition group-hover:-translate-x-1">Cap</span>
        <span className="font-medium text-gray-12 transition group-hover:translate-x-1">Buddy</span>
      </Link>

      <div className="flex items-center gap-2">
        <ChangeThemeForm userPreference={theme} id={id} />
        {children}
      </div>
    </div>
  )
}

function MobileNav({ teams, theme }: { teams: TeamsProp[]; theme: Theme | null }) {
  const user = useOptionalUser()
  // const submit = useSubmit()
  const formRef = useRef<HTMLFormElement>(null)

  const navLinkClass =
    'text-body-lg focusable inline-flex h-10 items-center justify-center rounded-md py-2 font-medium text-gray-12 underline-offset-4 outline-none hover:underline disabled:pointer-events-none disabled:opacity-50'

  return (
    <Dialog>
      <MobileNavHeader theme={theme} id="mobile-closed">
        <DialogTrigger asChild>
          <Button
            variant="secondary"
            size="icon"
            className="border-gray-7 bg-transparent text-gray-11"
          >
            <Icon name="hamburger-menu">
              <span className="sr-only">Open navigation menu</span>
            </Icon>
          </Button>
        </DialogTrigger>
      </MobileNavHeader>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 bg-gray-1">
          <DialogContent className="container absolute z-50 h-full w-full py-6">
            <nav className="flex flex-col">
              <MobileNavHeader theme={theme} id="mobile-open">
                <DialogClose asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="border-gray-7 bg-transparent text-gray-11"
                  >
                    <Icon name="cross-1">
                      <span className="sr-only">Close navigation menu</span>
                    </Icon>
                  </Button>
                </DialogClose>
              </MobileNavHeader>
              <Spacer size="4xs" />
              <div>
                <ul>
                  <li>
                    <DialogClose asChild>
                      <Link to="/draft" className={navLinkClass}>
                        Draft
                      </Link>
                    </DialogClose>
                  </li>
                  {/* <li>
                    <DialogClose asChild>
                      <Link to="/players" className={navLinkClass}>
                        FAQ
                      </Link>
                    </DialogClose>
                  </li> */}
                  <li>
                    <DialogClose asChild>
                      <Link to="/players" className={navLinkClass}>
                        Players
                      </Link>
                    </DialogClose>
                  </li>
                  <Collapsible asChild open={true}>
                    <li>
                      <CollapsibleTrigger asChild>
                        <button>
                          <Icon name="chevron-down" side="right">
                            <span className={navLinkClass}>Teams</span>
                          </Icon>
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <ul className="mb-4">
                          {teams.map(({ id, city, teamLogos }) => (
                            <li key={id}>
                              <DialogClose asChild>
                                <Link
                                  prefetch="intent"
                                  to={`/teams/${id}`}
                                  className="flex items-center p-2 pr-8"
                                >
                                  <TeamLogo className="mr-2 h-5" logo={teamLogos[0]} />
                                  <span className="text-body">{city}</span>
                                </Link>
                              </DialogClose>
                            </li>
                          ))}
                        </ul>
                      </CollapsibleContent>
                    </li>
                  </Collapsible>
                  {user != null && (
                    <Collapsible asChild>
                      <li className="border-t border-gray-6">
                        <CollapsibleTrigger asChild>
                          <button>
                            <Icon name="chevron-down" side="right">
                              <span className={navLinkClass}>{user.username}</span>
                            </Icon>
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent asChild>
                          <ul>
                            <DialogClose asChild>
                              <li>
                                <Link prefetch="intent" to="/settings/profile" className="p-2 pr-8">
                                  <Icon className="text-body" name="avatar">
                                    Profile
                                  </Icon>
                                </Link>
                              </li>
                            </DialogClose>
                            <DialogClose asChild>
                              <li>
                                <Form
                                  action="/logout"
                                  method="POST"
                                  ref={formRef}
                                  className="p-2 pr-8"
                                >
                                  <Icon className="text-body" name="exit">
                                    <button type="submit">Logout</button>
                                  </Icon>
                                </Form>
                              </li>
                            </DialogClose>
                          </ul>
                        </CollapsibleContent>
                      </li>
                    </Collapsible>
                  )}
                </ul>
              </div>
            </nav>
          </DialogContent>
        </DialogOverlay>
      </DialogPortal>
    </Dialog>
  )
}

function DesktopNav({ teams, theme }: { teams: TeamsProp[]; theme: Theme | null }) {
  const user = useOptionalUser()
  const submit = useSubmit()
  const formRef = useRef<HTMLFormElement>(null)

  const navLinkClass =
    'text-body focusable inline-flex h-10 items-center justify-center rounded-md px-4 py-2 font-medium text-gray-12 underline-offset-4 outline-none hover:underline disabled:pointer-events-none disabled:opacity-50'

  return (
    <nav className="flex w-full flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
      <Link to="/" className="focusable group grid leading-snug">
        <span className="font-light text-accent-11 transition group-hover:-translate-x-1">Cap</span>
        <span className="font-medium text-gray-12 transition group-hover:translate-x-1">Buddy</span>
      </Link>
      <ul className="flex items-center">
        <li>
          <Link prefetch="intent" to="/draft" className={navLinkClass}>
            Draft
          </Link>
        </li>
        {/* <li>
          <Link prefetch="intent" to="/faq" className={navLinkClass}>
            FAQ
          </Link>
        </li> */}
        <li>
          <Link prefetch="intent" to="/players" className={navLinkClass}>
            Players
          </Link>
        </li>
        <li>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="link" className="text-gray-12">
                <Icon className="" name="chevron-down" side="right">
                  <span className="text-body font-medium">Teams</span>
                </Icon>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent sideOffset={0} align="end">
              <ul>
                {teams.map(({ id, city, teamLogos }) => (
                  <li key={id}>
                    <DropdownMenuItem asChild>
                      <Link prefetch="intent" to={`/teams/${id}`} className="p-2 pr-8">
                        <TeamLogo className="mr-2 h-5" logo={teamLogos[0]} />
                        <span className="text-body">{city}</span>
                      </Link>
                    </DropdownMenuItem>
                  </li>
                ))}
              </ul>
            </DropdownMenuContent>
          </DropdownMenu>
        </li>

        <ChangeThemeForm userPreference={theme} id="desktop" />

        <li>
          {user != null && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button asChild variant="default">
                  <Link
                    to="/settings/profile"
                    // this is for progressive enhancement
                    onClick={(e) => e.preventDefault()}
                    className="ml-4 flex items-center gap-2"
                  >
                    <Icon name="chevron-down" side="right">
                      <span className="text-body font-medium">{user.username}</span>
                    </Icon>
                  </Link>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent sideOffset={8} align="start">
                <DropdownMenuItem asChild>
                  <Link prefetch="intent" to="/settings/profile" className="p-2 pr-8">
                    <Icon className="text-body" name="avatar">
                      Profile
                    </Icon>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  asChild
                  // this prevents the menu from closing before the form submission is completed
                  onSelect={(event) => {
                    event.preventDefault()
                    submit(formRef.current)
                  }}
                >
                  <Form action="/logout" method="POST" ref={formRef} className="p-2 pr-8">
                    <Icon className="text-body" name="exit">
                      <button type="submit">Logout</button>
                    </Icon>
                  </Form>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </li>
      </ul>
    </nav>
  )
}

export function Navbar(props: { teams: TeamsProp[]; theme: Theme | null }) {
  return (
    <header className="container max-w-6xl py-6">
      <div className="md:hidden">
        <MobileNav {...props} />
      </div>
      <div className="hidden md:block">
        <DesktopNav {...props} />
      </div>
    </header>
  )
}
